import { motion } from "framer-motion";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { blogPosts } from "@/data/blogPosts";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import googlePlayBadge from "@/assets/Google_Play.png";
import appStoreBadge from "@/assets/App_Store.png";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "TodoTomorrow",
      "logo": {
        "@type": "ImageObject",
        "url": "https://todotomorrow.com/logo.png"
      }
    },
    "url": `https://www.todotomorrow.com/blog/${post.slug}`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.todotomorrow.com/blog/${post.slug}`
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title} - TodoTomorrow Blog</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content="productivity, email workflow, task management, TodoTomorrow" />
        <meta name="author" content={post.author} />
        <link rel="canonical" href={`https://www.todotomorrow.com/blog/${post.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:url" content={`https://www.todotomorrow.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen pt-20 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6">
        <article className="container mx-auto max-w-3xl">
          {/* Back Button */}
          <motion.nav
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-6 sm:mb-8"
            aria-label="Breadcrumb"
          >
            <Link to="/blog">
              <Button variant="ghost" className="gap-2 text-sm sm:text-base">
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                Back to Blog
              </Button>
            </Link>
          </motion.nav>

          {/* Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 sm:mb-12"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 sm:mb-6 leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                <span>{post.readTime}</span>
              </div>
              <span>By {post.author}</span>
            </div>
          </motion.header>

          {/* Article Content */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-base sm:prose-lg prose-slate dark:prose-invert max-w-none"
          >
            <div 
              className="text-foreground leading-relaxed space-y-4 sm:space-y-6"
              dangerouslySetInnerHTML={{ 
                __html: post.content
                  .split('\n\n')
                  .map(paragraph => {
                    // Handle headings
                    if (paragraph.startsWith('## ')) {
                      return `<h2 class="text-xl sm:text-2xl font-semibold mt-8 sm:mt-12 mb-3 sm:mb-4 text-foreground">${paragraph.slice(3)}</h2>`;
                    }
                    // Handle regular paragraphs
                    return `<p class="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">${paragraph.trim()}</p>`;
                  })
                  .join('')
              }}
            />
          </motion.section>

          {/* Call to Action */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-2xl bg-secondary border border-border"
            aria-label="Try TodoTomorrow"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 text-foreground">
              Try TodoTomorrow
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
              Capture on the go, delivered to your inbox. Free during launch.
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a 
                href="https://play.google.com/store/apps/details?id=com.todotomorrow.app" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity"
                aria-label="Download TodoTomorrow on Google Play"
              >
                <img 
                  src={googlePlayBadge}
                  alt="Get it on Google Play" 
                  className="h-12 sm:h-14 w-auto"
                />
              </a>
              <a 
                href="https://apps.apple.com/ie/app/todotomorrow-daily-todo-list/id6755288345" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity"
                aria-label="Download TodoTomorrow on the App Store"
              >
                <img 
                  src={appStoreBadge}
                  alt="Download on the App Store" 
                  className="h-12 sm:h-14 w-auto"
                />
              </a>
            </div>
          </motion.aside>

          {/* Back to Blog Link */}
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-8 sm:mt-12 text-center"
            aria-label="Return to blog"
          >
            <Link to="/blog" className="text-sm sm:text-base text-primary hover:underline">
              ← Back to all posts
            </Link>
          </motion.nav>
        </article>
      </main>
    </>
  );
};

export default BlogPost;
